/*
 * SimTagPanel.
 *
 * Sits on SimDetailPage between the metric grid and the recipients table.
 * One row per canonical tag this template carries, with two stacked bars:
 *   - "This sim": clicks / sends across SEND_LOG entries for simName
 *   - "Other sims with this tag": clicks / sends across peers
 *   Both over the selected period.
 * Bars share the visual language of BarSegmentCard so it lives naturally
 * with the rest of the dashboard.
 *
 * Postgres mapping: see tagAnalytics.js header. Peer view can be a
 * server-side percentile so this query stays cheap.
 */
import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useThemeContext } from 'themes/ThemeContext';
import { motion } from 'motion/react';
import MainCard from '../../../components/cards/MainCard';
import CountUp from 'react-countup';
import { SEND_LOG } from '../data';
import { logInPeriod } from '../hooks/useDashboardData';
import { tagsForSim, TAG_LABELS, TAG_DESCRIPTIONS } from '../tagAnalytics';

const fadeUp = { hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const isClick = (s) => s === 'clicked' || s === 'clicked_2nd';

// Over the selected period (BD-1003), for both this sim and its peers.
export function ratesForSim(simName, log) {
    const tags = tagsForSim(simName);
    if (tags.length === 0) return [];

    const thisEvents = log.filter((e) => e.simName === simName);
    const thisClicks = thisEvents.filter((e) => isClick(e.status)).length;
    const thisRate = thisEvents.length ? (thisClicks / thisEvents.length) * 100 : 0;

    return tags.map((tag) => {
        let sends = 0;
        let clicks = 0;
        for (const e of log) {
            if (e.simName === simName) continue;
            if (!tagsForSim(e.simName).includes(tag)) continue;
            sends++;
            if (isClick(e.status)) clicks++;
        }
        const peerRate = sends ? (clicks / sends) * 100 : 0;
        return { tag, label: TAG_LABELS[tag] || tag, thisRate, peerRate, peerSends: sends, peerClicks: clicks, thisSends: thisEvents.length, thisClicks };
    });
}

function rateColor(rate) {
    if (rate >= 30) return 'var(--red)';
    if (rate >= 15) return 'var(--amber)';
    return 'var(--green)';
}

function ShimmerBar({ color, opacity = 1, pct, delay, isDark, height = 18 }) {
    const [value, setValue] = useState(0);

    useEffect(() => {
        setValue(0);
        const t = setTimeout(() => setValue(pct), 150 + delay * 1000);
        return () => clearTimeout(t);
    }, [pct, delay]);

    return (
        <LinearProgress
            variant="determinate"
            value={value}
            sx={{
                height,
                borderRadius: 1,
                backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'grey.100',
                '& .MuiLinearProgress-bar': {
                    borderRadius: 1,
                    background: color,
                    opacity,
                    transition: 'transform 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
                    transitionDelay: `${delay}s`,
                },
            }}
        />
    );
}

export default function SimTagPanel({ simName, period, startDate, endDate }) {
    const theme = useTheme();
    const { themeMode } = useThemeContext();
    const isDark = themeMode === 'dark';

    const rows = ratesForSim(simName, logInPeriod(SEND_LOG, { period, startDate, endDate }));
    if (rows.length === 0) return null;

    const max = Math.max(1, ...rows.flatMap((r) => [r.thisRate, r.peerRate]));

    return (
        <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                <Box sx={{ p: 3 }}>
                    <Box sx={{ mb: 2.5 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                            What this sim tests
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                            Each row is a lure tag this template carries. The top bar is this sim's click rate. The bottom bar is the average across every other sim that shares the tag.
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {rows.map((r, i) => {
                            const thisPct = (r.thisRate / max) * 100;
                            const peerPct = (r.peerRate / max) * 100;
                            const delta = r.thisRate - r.peerRate;
                            const thisColor = rateColor(r.thisRate);
                            return (
                                <Box key={r.tag}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                                        <Tooltip title={TAG_DESCRIPTIONS[r.tag] || r.label} placement="top" arrow>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', cursor: 'help', borderBottom: '1px dotted', borderColor: 'divider' }}>
                                                {r.label}
                                            </Typography>
                                        </Tooltip>
                                        {r.peerSends > 0 && (
                                            <Box sx={{
                                                display: 'inline-flex', alignItems: 'center',
                                                fontSize: 10, fontWeight: 700,
                                                px: '8px', py: '2px',
                                                borderRadius: '4px',
                                                bgcolor: delta > 5 ? 'var(--red-bg)' : delta < -5 ? 'var(--green-bg)' : 'action.hover',
                                                color: delta > 5 ? 'var(--red-text)' : delta < -5 ? 'var(--green-text)' : 'text.secondary',
                                            }}>
                                                {delta > 0 ? '+' : ''}{delta.toFixed(1)}% vs peer sims
                                            </Box>
                                        )}
                                    </Box>

                                    {/* This sim row */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '90px 1fr 64px', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.primary' }}>
                                            This sim
                                        </Typography>
                                        <ShimmerBar
                                            color={thisColor}
                                            pct={thisPct}
                                            delay={i * 0.08}
                                            isDark={isDark}
                                            height={20}
                                        />
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: thisColor, letterSpacing: '-0.01em', lineHeight: 1 }}>
                                                <CountUp end={r.thisRate} duration={1.2} decimals={1} />%
                                            </Typography>
                                            <Typography sx={{ fontSize: 9, color: 'text.disabled' }}>
                                                {r.thisClicks}/{r.thisSends}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Peer sims row */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '90px 1fr 64px', alignItems: 'center', gap: 1.5 }}>
                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>
                                            Peer sims
                                        </Typography>
                                        <ShimmerBar
                                            color={isDark ? 'rgba(255,255,255,0.35)' : 'rgba(107,117,145,0.45)'}
                                            pct={peerPct}
                                            delay={i * 0.08 + 0.15}
                                            isDark={isDark}
                                            height={20}
                                        />
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', letterSpacing: '-0.01em', lineHeight: 1 }}>
                                                {r.peerSends > 0 ? `${r.peerRate.toFixed(1)}%` : '-'}
                                            </Typography>
                                            <Typography sx={{ fontSize: 9, color: 'text.disabled' }}>
                                                {r.peerSends > 0 ? `${r.peerClicks}/${r.peerSends}` : 'no peers'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </MainCard>
        </motion.div>
    );
}
